/**
 * Block 12 calculator — `hingeCount`: количество петель (штук).
 *
 * 3 anketa variants (Кухня, Шкаф, Балкон) — остальные 4 (Тумба-ванна,
 * Антресоль, Шкаф-санузел, ТВ-зона) принимают hinge как user input.
 *
 * **Округление:**
 * - Кухня — `2 × facadeCount` (наследует `Math.round` от facadeCount)
 * - Шкаф / Балкон — `ceilToEven` (per OQ-5)
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.X В.2 / §4.7.
 */

import type { CalculatorParams } from '../../contracts/calculator';
import { ceilToEven } from '../utils/ceilToEven';
import { mmToM } from '../utils/mmToM';
import { facadeCountKitchen } from './facadeCount';
import {
  assertPositiveDimension,
  type BalconyParams,
  type CabinetParams,
  type KitchenParams,
} from './types';

/**
 * Кухня: `hingeCount = 2 × facadeCount` (нативно в Кухне петли = 2 × фасады).
 *
 * Округление наследуется от `facadeCountKitchen` (Math.round per OQ-1).
 *
 * **Источник:** xlsx `Кухня!A47-E47`.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.1 В.2 + §4.7.
 *
 * @example
 *   L=5000, H=2900, no penal/shelf:
 *     facadeCount = 28 → hingeCount = 56
 */
export function hingeCountKitchen(params: KitchenParams): number {
  return 2 * facadeCountKitchen(params);
}

/**
 * Шкаф / Гардероб:
 * - прямой: `(L_м/0.5) × (H_м/0.45)`, округление `ceilToEven`
 * - угловой: `((L_м+L2_м-0.6)/0.5) × (H_м/0.45)`, округление `ceilToEven`
 *
 * **Источник:** xlsx `Шкаф!C38` (прямой), `D38` (угловой) + OQ-5.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.2 В.2 + §4.7.
 *
 * ⚠️ **xlsx error (B-097):** `Шкаф!D38` пример «56 петель» для L=3, L2=2, H=2.9
 *    неверный; Aibek OQ-5: `ceilToEven(56.7) = 58`. Эта функция реализует
 *    **правило**, не xlsx-пример.
 *
 * @throws Error если угловой и `L+L2 ≤ 600`.
 * @example
 *   прямой L=2000, H=2700 → (2/0.5) × (2.7/0.45) = 4 × 6 = 24 → ceilToEven = 24
 *   угловой L=3000, L2=2000, H=2900:
 *     ((3+2-0.6)/0.5) × (2.9/0.45) = 8.8 × 6.444... = 56.7 → ceilToEven = 58
 */
export function hingeCountCabinet(params: CabinetParams): number {
  assertPositiveDimension(params.length, 'hingeCountCabinet.length');
  assertPositiveDimension(params.height, 'hingeCountCabinet.height');

  const Hm = mmToM(params.height);
  let totalLm: number;
  if (params.isAngular) {
    assertPositiveDimension(params.length2, 'hingeCountCabinet.length2 (isAngular=true)');
    totalLm = mmToM(params.length) + mmToM(params.length2) - 0.6;
    if (totalLm <= 0) {
      throw new Error(
        `hingeCountCabinet: angular L+L2 must be > 600mm, got L=${params.length}, L2=${params.length2}`,
      );
    }
  } else {
    totalLm = mmToM(params.length);
  }

  return ceilToEven((totalLm / 0.5) * (Hm / 0.45));
}

/**
 * Балкон: формула идентична Шкафу (per методике §2.7 В.2):
 * - прямой: `(L_м/0.5) × (H_м/0.45)`
 * - угловой: `((L_м+L2_м-0.6)/0.5) × (H_м/0.45)`
 *
 * Угловой определяется по наличию `length2`. Округление: `ceilToEven`.
 *
 * **Источник:** xlsx `Балкон!C21` (прямой), `D21` (угловой) + OQ-5.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.7 В.2 + §4.7.
 */
export function hingeCountBalcony(params: BalconyParams): number {
  assertPositiveDimension(params.length, 'hingeCountBalcony.length');
  assertPositiveDimension(params.height, 'hingeCountBalcony.height');

  const Hm = mmToM(params.height);
  const isAngular = typeof params.length2 === 'number';
  let totalLm: number;
  if (isAngular) {
    assertPositiveDimension(params.length2, 'hingeCountBalcony.length2');
    totalLm = mmToM(params.length) + mmToM(params.length2) - 0.6;
    if (totalLm <= 0) {
      throw new Error(
        `hingeCountBalcony: angular L+L2 must be > 600mm, got L=${params.length}, L2=${params.length2}`,
      );
    }
  } else {
    totalLm = mmToM(params.length);
  }

  return ceilToEven((totalLm / 0.5) * (Hm / 0.45));
}

/**
 * Top-level dispatcher: возвращает hingeCount для anketa с автоматической
 * формулой, throws для anketa с user-input handle/hinge.
 */
export function hingeCount(params: CalculatorParams): number {
  switch (params.type) {
    case 'kitchen':
      return hingeCountKitchen(params);
    case 'cabinet':
      return hingeCountCabinet(params);
    case 'balcony':
      return hingeCountBalcony(params);
    case 'bath-tabletop':
    case 'bath-shelf-above':
    case 'bath-cabinet':
    case 'tv-zone':
      throw new Error(
        `hingeCount: не применяется к "${params.type}" (hinge — user input per методике §В.2)`,
      );
    case 'other':
      throw new Error('hingeCount: не применяется к "Другая мебель" (свободный режим)');
  }
}
